// src/contexts/TutorRuntimeContext.jsx — hoists the tutor runtime above
// the route layer so the conversation persists across page navigations.
//
// Without this, the runtime lived in `<TutorChat>` (a route element).
// Every navigation unmounted the component, killed the assistant-ui
// runtime, and forced a fresh /tutor/start on the next visit. The
// "tutor follows me across surfaces" vision needs the runtime to be
// shell-level, not route-level.
//
// What this provider exposes:
//   runtime       — assistant-ui runtime (pass to AssistantRuntimeProvider)
//   sessionId     — backend tutor session id (panel uses for fresh-start)
//   proposal      — current CourseProposal capture (panel renders card)
//   pendingAction — current PendingAction capture (panel renders button)
//   resetCapture  — clear proposal + pendingAction (after navigation)
//   error / booting — boot-time signals
//   routeContext  — `{course_code, domain, concept, surface}` parsed from URL
//   panelOpen     — bool, persisted in localStorage
//   togglePanel   — flip + persist
//   openPanel     — explicit open (e.g. when a deep-link asks for it)
//   closePanel    — explicit close
//
// Keyboard shortcut: Cmd/Ctrl + . toggles the panel from anywhere.
// localStorage key 'tutor:panelOpen' persists across reloads.
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';

import useTutorRuntime from '../components/chat/useTutorRuntime';
import { useRouteContext } from '../hooks/useRouteContext';

const PANEL_OPEN_KEY = 'tutor:panelOpen';

function readInitialOpen() {
  try {
    const v = localStorage.getItem(PANEL_OPEN_KEY);
    if (v === null) return true; // first-time default: panel open + welcoming
    return v === '1';
  } catch {
    return true;
  }
}

const TutorRuntimeCtx = createContext(null);

export function TutorRuntimeProvider({ children }) {
  // Snapshot the route context at the moment the provider mounts.
  // Used as `bootContext` for /tutor/start. The runtime boots ONCE
  // per app session; subsequent navigation updates `routeContext`
  // but doesn't re-boot. Per-turn context goes through `getRouteContext`
  // (M0.1) so the live URL flows on every send.
  const initialRouteRef = useRef(null);
  if (initialRouteRef.current === null) {
    // Compute once. We can't call useRouteContext() conditionally,
    // so we read it below and only memoize the very first value.
  }

  const routeContext = useRouteContext();
  if (initialRouteRef.current === null && routeContext) {
    initialRouteRef.current = routeContext;
  }

  // Live route-context ref so onNew (closed-over by useExternalStoreRuntime
  // on first render) can read the CURRENT URL surface at call-time. Keeps
  // the runtime singleton while still letting per-turn context flow.
  const routeContextRef = useRef(routeContext);
  useEffect(() => { routeContextRef.current = routeContext; }, [routeContext]);
  const getRouteContext = useCallback(() => routeContextRef.current, []);

  // M3: useLangGraphRuntime under the hood. Must be called exactly
  // once at shell level — re-mounting would re-trigger startTutor and
  // create a fresh session. The Provider lives outside <Routes> so
  // navigation never remounts it.
  const {
    runtime, sessionId, proposal, pendingAction, pendingCurriculumEdit,
    error, booting, resetCapture,
  } = useTutorRuntime({
    bootContext: initialRouteRef.current,
    getRouteContext,
  });

  const [panelOpen, setPanelOpenState] = useState(readInitialOpen);

  const setPanelOpen = useCallback((next) => {
    setPanelOpenState((prev) => {
      const v = typeof next === 'function' ? next(prev) : next;
      try {
        localStorage.setItem(PANEL_OPEN_KEY, v ? '1' : '0');
      } catch {}
      return v;
    });
  }, []);
  const togglePanel = useCallback(() => setPanelOpen((p) => !p), [setPanelOpen]);
  const openPanel = useCallback(() => setPanelOpen(true), [setPanelOpen]);
  const closePanel = useCallback(() => setPanelOpen(false), [setPanelOpen]);

  // Conversation gravity: any surface can open the panel AND start a
  // real turn ("Ask the tutor about <this>"). runtime.thread.append is
  // assistant-ui's documented send API; if it ever regresses we surface
  // a console.warn and still open the panel — never a silent dead button.
  const sendToTutor = useCallback((text) => {
    setPanelOpen(true);
    try {
      runtime.thread.append({ role: 'user', content: [{ type: 'text', text }] });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('sendToTutor: append unavailable, panel opened without seed', err);
    }
  }, [runtime, setPanelOpen]);

  // Cmd/Ctrl + . global toggle. Avoid conflicts with text inputs by
  // only listening on the document level (the keystroke isn't a
  // common in-textarea binding).
  const togglePanelRef = useRef(togglePanel);
  useEffect(() => { togglePanelRef.current = togglePanel; }, [togglePanel]);
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === '.') {
        e.preventDefault();
        togglePanelRef.current();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const value = useMemo(
    () => ({
      runtime, sessionId, proposal, pendingAction, pendingCurriculumEdit,
      error, booting, resetCapture,
      routeContext,
      panelOpen, setPanelOpen, togglePanel, openPanel, closePanel,
      sendToTutor,
    }),
    [
      runtime, sessionId, proposal, pendingAction, pendingCurriculumEdit,
      error, booting, resetCapture,
      routeContext,
      panelOpen, setPanelOpen, togglePanel, openPanel, closePanel,
      sendToTutor,
    ],
  );

  return <TutorRuntimeCtx.Provider value={value}>{children}</TutorRuntimeCtx.Provider>;
}

export function useTutor() {
  const ctx = useContext(TutorRuntimeCtx);
  if (!ctx) throw new Error('useTutor must be used inside TutorRuntimeProvider');
  return ctx;
}
